import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";
import { useCategories } from "../../../../hooks/useCategories";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

interface ProductRow {
  id: string;
  title: string;
  type: "video" | "kit";
  price: number;
  thumbnail_url: string | null;
  visibility: string;
  status: string;
  sales_count: number;
  stock_count: number;
  created_at: string;
  description: string | null;
  category: string | null;
}

interface EditModal {
  product: ProductRow;
  onClose: () => void;
  onSaved: () => void;
}

function ProductEditModal({ product, onClose, onSaved }: EditModal) {
  const { categories } = useCategories();
  const [title, setTitle] = useState(product.title);
  const [price, setPrice] = useState(String(product.price));
  const [description, setDescription] = useState(product.description || "");
  const [category, setCategory] = useState(product.category || "");
  const [stockCount, setStockCount] = useState(String(product.stock_count ?? 0));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!title.trim()) { setError("タイトルを入力してください。"); return; }
    if (isNaN(parseInt(price)) || parseInt(price) < 0) { setError("正しい価格を入力してください。"); return; }
    setSaving(true);
    const updates: Record<string, unknown> = {
      title: title.trim(),
      price: parseInt(price),
      description: description.trim(),
      category: category || null,
      updated_at: new Date().toISOString(),
    };
    if (product.type === "kit") {
      updates.stock_count = parseInt(stockCount) || 0;
    }
    const { error: err } = await supabase.from("products").update(updates).eq("id", product.id);
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved();
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-7 w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-gold text-xs font-display italic tracking-widest mb-1">Edit Product</p>
            <h3 className="font-serif text-deep-green text-lg">商品を編集</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-100 text-charcoal/40 cursor-pointer hover:text-charcoal">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-sans text-charcoal/60 mb-1.5">タイトル <span className="text-red-400">*</span></label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1.5">価格（円）</label>
              <input type="number" min="0" value={price} onChange={e => setPrice(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
            </div>
            {product.type === "kit" && (
              <div>
                <label className="block text-xs font-sans text-charcoal/60 mb-1.5">在庫数</label>
                <input type="number" min="0" value={stockCount} onChange={e => setStockCount(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-sans text-charcoal/60 mb-1.5">カテゴリ</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors bg-white cursor-pointer">
              <option value="">未設定</option>
              {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-sans text-charcoal/60 mb-1.5">説明文</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} maxLength={500} rows={4}
              className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal resize-none transition-colors" />
            <p className="text-right text-xs text-charcoal/30 font-sans mt-0.5">{description.length}/500</p>
          </div>
          {error && (
            <div className="text-xs text-red-500 font-sans bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              <i className="ri-error-warning-line mr-1" />{error}
            </div>
          )}
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-3 rounded-full border border-stone-200 text-sm font-sans text-charcoal/60 cursor-pointer hover:border-stone-300">キャンセル</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-3 rounded-full bg-deep-green text-white text-sm font-sans cursor-pointer hover:bg-teal transition-colors disabled:opacity-60 whitespace-nowrap">
            {saving ? "保存中..." : "変更を保存 →"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface BuyerPurposeModalProps {
  productId: string;
  productTitle: string;
  onClose: () => void;
}

const PURPOSE_LABELS: Record<string, string> = {
  hobby: "趣味として学びたい",
  skill: "手に職をつけたい",
  career: "将来の仕事につなげたい",
  return: "復職の準備をしたい",
  change: "転職・キャリアチェンジ",
  business: "独立・副業の参考に",
  succession: "家業・事業承継",
  research: "情報収集したい",
  other: "その他",
  unknown: "未設定",
};

const CHART_COLORS = ["#2D6A4F", "#C9A84C", "#4ECDC4", "#F4A261", "#A8DADC", "#6B7280", "#BE185D", "#7C3AED", "#DC2626"];

function BuyerPurposeModal({ productId, productTitle, onClose }: BuyerPurposeModalProps) {
  const [data, setData] = useState<{ name: string; value: number }[]>([]);
  const [totalBuyers, setTotalBuyers] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      // Get purchases for this product
      const { data: purchases } = await supabase
        .from("purchases")
        .select("buyer_id")
        .eq("product_id", productId)
        .eq("status", "completed");

      if (!purchases || purchases.length === 0) {
        setLoading(false);
        return;
      }

      setTotalBuyers(purchases.length);

      // Get buyer profiles
      const buyerIds = [...new Set(purchases.map(p => p.buyer_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("purpose")
        .in("id", buyerIds);

      // Count purposes
      const counts: Record<string, number> = {};
      (profiles || []).forEach(p => {
        const key = p.purpose || "unknown";
        counts[key] = (counts[key] || 0) + 1;
      });

      const chartData = Object.entries(counts)
        .map(([key, value]) => ({ name: PURPOSE_LABELS[key] || key, value }))
        .sort((a, b) => b.value - a.value);

      setData(chartData);
      setLoading(false);
    };
    load();
  }, [productId]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-7 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-teal text-xs font-display italic tracking-widest mb-1">Buyer Analysis</p>
            <h3 className="font-serif text-deep-green text-base">購入者の利用目的分布</h3>
            <p className="text-xs text-charcoal/50 font-sans mt-0.5 truncate max-w-[260px]">{productTitle}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-100 text-charcoal/40 cursor-pointer hover:text-charcoal">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><i className="ri-loader-4-line animate-spin text-2xl text-teal" /></div>
        ) : totalBuyers === 0 ? (
          <div className="text-center py-10">
            <i className="ri-user-line text-4xl text-charcoal/20 block mb-3" />
            <p className="text-charcoal/40 font-sans text-sm">まだ購入者がいません</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 bg-stone-50 rounded-xl px-4 py-2.5 mb-4">
              <i className="ri-group-line text-teal" />
              <p className="text-sm font-sans text-charcoal">購入者数：<strong className="text-deep-green">{totalBuyers}名</strong></p>
            </div>
            {data.length > 0 && (
              <div className="h-48 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {data.map((_, index) => (
                        <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => [`${value}名`, ""]} />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      formatter={(value) => <span style={{ fontSize: "11px", color: "#555" }}>{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="space-y-2">
              {data.map((item, index) => (
                <div key={item.name} className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                  <p className="text-xs font-sans text-charcoal flex-1 truncate">{item.name}</p>
                  <p className="text-xs font-sans text-charcoal/60 font-medium">{item.value}名</p>
                  <div className="w-16 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(item.value / totalBuyers) * 100}%`, backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ProviderProducts() {
  const { currentUser } = useApp();
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"video" | "kit">("video");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingProduct, setEditingProduct] = useState<ProductRow | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [analyzeProduct, setAnalyzeProduct] = useState<{ id: string; title: string } | null>(null);

  const planInfo = { slots: currentUser?.plan === "light" ? 999 : 3, extra: 0 };
  const videoCount = products.filter((p) => p.type === "video").length;
  const remainingSlots = Math.max(0, planInfo.slots - videoCount + planInfo.extra);

  const load = async () => {
    if (!currentUser?.id) { setLoading(false); return; }
    const { data } = await supabase
      .from("products")
      .select("id,title,type,price,thumbnail_url,visibility,status,sales_count,stock_count,created_at,description,category")
      .eq("provider_id", currentUser.id)
      .order("created_at", { ascending: false });
    setProducts(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [currentUser?.id]);

  const toggleVisibility = async (id: string, current: string) => {
    const newVis = current === "public" ? "draft" : "public";
    await supabase.from("products").update({ visibility: newVis }).eq("id", id);
    setProducts((prev) => prev.map((p) => p.id === id ? { ...p, visibility: newVis } : p));
  };

  const deleteProduct = async (id: string) => {
    if (!confirm("この商品を削除しますか？この操作は取り消せません。")) return;
    setDeletingId(id);
    await supabase.from("products").update({ visibility: "draft", status: "hidden" }).eq("id", id);
    setProducts((prev) => prev.filter((p) => p.id !== id));
    setDeletingId(null);
  };

  const filtered = products.filter((p) => p.type === activeTab);

  if (loading) return <div className="flex justify-center py-10"><i className="ri-loader-4-line animate-spin text-2xl text-teal" /></div>;

  return (
    <div className="space-y-5">
      {currentUser?.plan !== "light" && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="flex-1">
            <p className="text-sm font-sans font-medium text-amber-800">
              <i className="ri-information-line mr-1" />
              フリープラン：残り動画投稿枠 {remainingSlots}/{planInfo.slots} 本
            </p>
            <p className="text-xs text-amber-700 font-sans mt-1">
              追加枠（月額1,500円/本）またはライトプランへのアップグレードで無制限になります。
            </p>
          </div>
          <Link to="/mypage/provider" className="text-xs bg-deep-green text-white px-3 py-2 rounded-full font-sans whitespace-nowrap cursor-pointer hover:bg-teal transition-colors">プランを見る</Link>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-stone-100 p-1 rounded-xl">
          {[{ v: "video" as const, l: "動画講座" }, { v: "kit" as const, l: "教材キット" }].map((t) => (
            <button key={t.v} onClick={() => setActiveTab(t.v)}
              className={`px-4 py-2 text-xs font-sans rounded-lg transition-all cursor-pointer whitespace-nowrap ${activeTab === t.v ? "bg-white text-deep-green" : "text-charcoal/50 hover:text-charcoal"}`}>
              {t.l} ({products.filter(p => p.type === t.v).length})
            </button>
          ))}
        </div>
        <Link to="/sell" className="text-xs bg-deep-green text-white px-4 py-2 rounded-full font-sans whitespace-nowrap cursor-pointer hover:bg-teal transition-colors">
          + 新規出品
        </Link>
      </div>

      <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <i className={`${activeTab === "video" ? "ri-video-line" : "ri-box-3-line"} text-4xl text-charcoal/20 block mb-3`} />
            <p className="text-charcoal/40 font-sans text-sm">まだ出品がありません</p>
            <Link to={`/sell/${activeTab === "video" ? "video" : "kit"}`} className="mt-3 inline-block text-teal text-sm font-sans cursor-pointer hover:underline">今すぐ出品する →</Link>
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {filtered.map((p) => (
              <div key={p.id} className="flex gap-4 p-4 items-center hover:bg-stone-50/50 transition-colors">
                <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-stone-100">
                  {p.thumbnail_url && <img src={p.thumbnail_url} alt={p.title} className="w-full h-full object-cover object-top" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm text-charcoal">{p.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-xs text-charcoal/40 font-sans">¥{p.price.toLocaleString()}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-sans ${p.visibility === "public" ? "bg-teal/10 text-teal" : "bg-stone-100 text-charcoal/40"}`}>
                      {p.visibility === "public" ? "公開中" : "非公開"}
                    </span>
                    <span className="text-xs text-charcoal/30 font-sans">{p.sales_count}件販売</span>
                  </div>
                  {savedId === p.id && (
                    <span className="text-xs text-teal font-sans flex items-center gap-1 mt-0.5">
                      <i className="ri-checkbox-circle-line" />保存しました
                    </span>
                  )}
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  {/* Buyer analysis button */}
                  <button
                    onClick={() => setAnalyzeProduct({ id: p.id, title: p.title })}
                    className="w-8 h-8 flex items-center justify-center rounded-full border border-stone-200 text-charcoal/50 cursor-pointer hover:border-teal hover:text-teal transition-colors"
                    title="購入者分析"
                  >
                    <i className="ri-pie-chart-2-line text-sm" />
                  </button>
                  <button
                    onClick={() => setEditingProduct(p)}
                    className="w-8 h-8 flex items-center justify-center rounded-full border border-stone-200 text-charcoal/50 cursor-pointer hover:border-teal hover:text-teal transition-colors"
                    title="編集する"
                  >
                    <i className="ri-edit-line text-sm" />
                  </button>
                  <button
                    onClick={() => toggleVisibility(p.id, p.visibility)}
                    className="w-8 h-8 flex items-center justify-center rounded-full border border-stone-200 text-charcoal/50 cursor-pointer hover:border-teal hover:text-teal transition-colors"
                    title={p.visibility === "public" ? "非公開にする" : "公開する"}
                  >
                    <i className={p.visibility === "public" ? "ri-eye-line text-sm" : "ri-eye-off-line text-sm"} />
                  </button>
                  <button
                    onClick={() => deleteProduct(p.id)}
                    disabled={deletingId === p.id}
                    className="w-8 h-8 flex items-center justify-center rounded-full border border-stone-200 text-charcoal/50 cursor-pointer hover:border-red-300 hover:text-red-400 transition-colors disabled:opacity-40"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingProduct && (
        <ProductEditModal
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSaved={() => {
            setSavedId(editingProduct.id);
            setEditingProduct(null);
            load();
            setTimeout(() => setSavedId(null), 3000);
          }}
        />
      )}

      {analyzeProduct && (
        <BuyerPurposeModal
          productId={analyzeProduct.id}
          productTitle={analyzeProduct.title}
          onClose={() => setAnalyzeProduct(null)}
        />
      )}
    </div>
  );
}
